import React, { useCallback, useEffect, useState, FC } from 'react'
import { ContentState, ContentBlock } from 'draft-js'
import ReactCrop, { Crop } from 'react-image-crop'
import image from 'styles/components/image.module.scss'
import text from 'styles/utility/text.module.scss'
import { useDispatch } from 'react-redux'
import { ImageModal } from 'components/Modal'
import { Spacer } from 'components/Utility'
import { FlexRow } from 'components/Flex'
import { BlueButton, GreyButton } from 'components/Button'
import { hideLoadingAction, showLoadingAction } from 'reducks/loading/actions'
import { uploadImageInWysiwygEditor } from 'lib/storage'

type Props = {
  contentState: ContentState
  block: ContentBlock
  blockProps: {
    canvas: HTMLCanvasElement
    folderPath: string
    replaceImage: (src: string, dist: string) => void
  }
}

const WysiwygMedia: FC<Props> = (props) => {
  const { contentState, block, blockProps } = props
  const { canvas, folderPath, replaceImage } = blockProps

  const emptyHtml = ' '
  const dispatch = useDispatch()
  const [src, setSrc] = useState('')
  const [imageRef, setImageRef] = useState<HTMLImageElement | null>(null)
  const [open, setOpen] = useState<boolean>(false)
  const [fileType, setFileType] = useState<string>('')
  const [crop, setCrop] = useState<Crop | undefined>(undefined)

  const onImageLoaded = (image: HTMLImageElement) => {
    setImageRef(image)
    // 画像の高さ・幅の100%で切り取り枠を表示
    const { height, width } = image
    setCrop({
      unit: 'px',
      width,
      height,
      x: 0,
      y: 0,
    })
    // iOS Safariの場合、画像アップロード直後にデフォルトcropするまでwaitしたいため
    dispatch(showLoadingAction())
    setTimeout(() => {
      dispatch(hideLoadingAction())
    }, 500)

    return false
  }

  const onCropChange = (crop: Crop) => {
    setCrop(crop)
  }

  const onCropComplete = (crop: Crop) => {
    if (imageRef && crop.width && crop.height) {
      const scaleX = imageRef.naturalWidth / imageRef.width
      const scaleY = imageRef.naturalHeight / imageRef.height
      canvas.width = crop.width
      canvas.height = crop.height
      const ctx = canvas.getContext('2d')
      if (ctx !== null) {
        ctx.drawImage(
          imageRef,
          (crop.x ?? 0) * scaleX,
          (crop.y ?? 0) * scaleY,
          crop.width * scaleX,
          crop.height * scaleY,
          0,
          0,
          crop.width,
          crop.height
        )
      }
    }
  }

  const closeModal = useCallback(() => {
    setFileType('')
    setOpen(false)
  }, [setOpen, setFileType])

  const saveCroppedImage = () => {
    canvas.toBlob(async (blob) => {
      if (!blob) return
      const dist = await uploadImageInWysiwygEditor(blob, fileType, folderPath)
      replaceImage(src, dist)
      closeModal()
    })
  }

  // 画像アップロード直後のバグを修正
  // iOS Safariの場合、Wait処理を入れないとデフォルト画像をうまく切り取れなかった
  useEffect(() => {
    setTimeout(() => {
      if (imageRef && crop && crop.width && crop.height) {
        const scaleX = imageRef.naturalWidth / imageRef.width
        const scaleY = imageRef.naturalHeight / imageRef.height
        canvas.width = crop.width
        canvas.height = crop.height

        const ctx = canvas.getContext('2d')
        if (ctx !== null) {
          ctx.drawImage(
            imageRef,
            (crop.x ?? 0) * scaleX,
            (crop.y ?? 0) * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            crop.width,
            crop.height
          )
        }
      }
    }, 500)
  }, [imageRef, crop])

  useEffect(() => {
    if (!contentState || !block) return
    // エンティティを持たないatomicブロックの場合、getEntityが例外を投げるため参照しない
    const entityKey = block.getEntityAt(0)
    if (!entityKey) return
    const data = contentState.getEntity(entityKey).getData()
    setSrc(data.src)
  }, [])

  return (
    <div>
      {emptyHtml}
      <img src={src} alt={''} style={{ maxWidth: '100%' }} onClick={() => setOpen(true)} />
      {src && open && (
        <ImageModal open={open} closeModal={closeModal}>
          <ReactCrop
            src={src}
            className={image.crop}
            crop={crop}
            ruleOfThirds
            onImageLoaded={onImageLoaded}
            onComplete={onCropComplete}
            onChange={onCropChange}
            crossorigin={'anonymous'}
          />
          <Spacer size={20} />
          <p className={`${text.font_bold} ${text.color_blue_main} ${text.align_center}`}>
            ※白枠の内側のみ保存されます
          </p>
          <Spacer size={20} />
          <FlexRow justifyContent={'center'}>
            <GreyButton label={'キャンセル'} onClick={closeModal} size={'small'} type={'outlined'} />
            <Spacer size={16} />
            <BlueButton label={'保存する'} onClick={saveCroppedImage} size={'small'} type={'main'} />
          </FlexRow>
        </ImageModal>
      )}
    </div>
  )
}

export default WysiwygMedia
